const CAREER_PAGES = [
  {
    slug: 'java-developer',
    title: 'Java Developer',
    aliases: ['java developer', 'java dev'],
    headline: 'Ace Your Java Developer Interview',
    description: 'Practice real Java interview questions — from core language concepts to system design. Get BARS-scored feedback so you know exactly where to improve.',
    questions: [
      'Explain the difference between an abstract class and an interface in Java.',
      'How does garbage collection work in the JVM?',
      'Tell me about a time you optimized a slow Java service in production.',
      'What is the difference between synchronized and volatile in Java?',
      'Describe your experience with Spring Boot and dependency injection.',
    ],
    competencies: ['Core Java', 'JVM & Memory', 'Spring/Hibernate', 'System Design', 'Concurrency'],
    faq: [
      { q: 'What topics are covered in Java developer interviews?', a: 'Java interviews typically cover core language (OOP, generics, collections), JVM internals, concurrency, Spring framework, SQL, and system design. Senior roles add distributed systems and architecture.' },
      { q: 'How many practice sessions do I need for a Java interview?', a: 'Most users see significant improvement in 5–7 sessions. Practicing daily for 1–2 weeks before your interview is enough to build confidence and structured answers.' },
    ],
  },
  {
    slug: 'product-manager',
    title: 'Product Manager',
    aliases: ['product manager', 'pm'],
    headline: 'Land Your Product Manager Role',
    description: 'Practice PM interviews with AI — product sense, behavioral, metrics, and case questions. Get structured feedback using the same BARS framework top companies use.',
    questions: [
      'How would you prioritize a backlog with 3 competing stakeholder requests?',
      'Design a product for elderly users to manage their medications.',
      'Tell me about a product you launched that underperformed. What did you learn?',
      'How would you measure the success of a new onboarding flow?',
      'Walk me through how you\'d approach a 20% drop in daily active users.',
    ],
    competencies: ['Product Sense', 'Metrics & Analytics', 'Prioritization', 'Stakeholder Management', 'Communication'],
    faq: [
      { q: 'What types of questions appear in PM interviews?', a: 'PM interviews include product sense questions (design a product), analytical questions (diagnose a metric drop), behavioral questions (tell me about a time…), and strategy questions. AI coaching helps you practice all types.' },
      { q: 'Do I need a technical background for PM interviews?', a: 'Not necessarily, but you need to understand engineering trade-offs at a high level. JobSkills tailors questions to your experience level — junior, mid, or senior PM.' },
    ],
  },
  {
    slug: 'data-engineer',
    title: 'Data Engineer',
    aliases: ['data engineer'],
    headline: 'Prepare for Data Engineer Interviews',
    description: 'Practice data engineering interview questions — pipelines, SQL, distributed systems, and behavioral scenarios. Get instant AI feedback on your answers.',
    questions: [
      'Walk me through how you\'d design a data pipeline for real-time analytics.',
      'What is the difference between a data lake and a data warehouse?',
      'How have you handled schema evolution in a production data pipeline?',
      'Tell me about a time a data pipeline failed. How did you debug it?',
      'Explain partitioning strategies in Apache Spark.',
    ],
    competencies: ['SQL & Data Modeling', 'Pipeline Design', 'Spark/Kafka', 'Cloud Platforms', 'Problem-Solving'],
    faq: [
      { q: 'What do data engineer interviews focus on?', a: 'Data engineering interviews test SQL (window functions, optimization), pipeline design (batch vs. streaming), tool knowledge (Spark, Airflow, Kafka), cloud platforms (AWS/GCP), and system design for large-scale data.' },
      { q: 'How is a data engineer interview different from a software engineer interview?', a: 'Data engineer interviews lean heavily on SQL, pipeline architecture, and data modeling rather than algorithms. Behavioral questions still follow STAR structure, which AI coaching helps you practice.' },
    ],
  },
  {
    slug: 'frontend-developer',
    title: 'Frontend Developer',
    aliases: ['frontend developer', 'front end developer', 'react developer'],
    headline: 'Ace Your Frontend Developer Interview',
    description: 'Practice frontend interview questions — React, JavaScript, CSS, performance, and behavioral. Get BARS-scored AI feedback on both technical and soft-skill answers.',
    questions: [
      'Explain the Virtual DOM and why React uses it.',
      'How would you optimize a React app that is rendering slowly?',
      'Describe the difference between controlled and uncontrolled components.',
      'Tell me about a UI bug you fixed that was particularly hard to reproduce.',
      'How do you approach cross-browser compatibility issues?',
    ],
    competencies: ['JavaScript/TypeScript', 'React/Vue', 'CSS & Performance', 'Web Accessibility', 'Communication'],
    faq: [
      { q: 'What topics do frontend developer interviews cover?', a: 'Frontend interviews cover JavaScript fundamentals, React (hooks, state management, rendering), CSS layout, web performance, accessibility, and behavioral questions. Senior roles add system design for UIs.' },
      { q: 'Should I expect coding questions in a frontend interview?', a: 'Most frontend interviews include a live coding round and a behavioral round. JobSkills focuses on the behavioral and system design portion — the questions you practice with AI are the ones that determine culture fit and seniority level.' },
    ],
  },
  {
    slug: 'backend-developer',
    title: 'Backend Developer',
    aliases: ['backend developer', 'back end developer'],
    headline: 'Prepare for Backend Developer Interviews',
    description: 'Practice backend interview questions — APIs, databases, system design, and behavioral. Get structured AI feedback to improve your answers before the real interview.',
    questions: [
      'Design a REST API for a ride-sharing app. What endpoints would you create?',
      'How do you handle database migrations in a zero-downtime deployment?',
      'Tell me about a service you built that needed to scale under high load.',
      'What is the N+1 query problem and how do you fix it?',
      'Explain the trade-offs between synchronous and asynchronous processing.',
    ],
    competencies: ['API Design', 'Databases & SQL', 'System Design', 'Scalability', 'Problem-Solving'],
    faq: [
      { q: 'What do backend developer interviews test?', a: 'Backend interviews cover API design, databases (SQL and NoSQL), distributed systems, caching, message queues, security, and system design. Behavioral questions assess collaboration and past project impact.' },
      { q: 'How important are behavioral questions for backend roles?', a: 'Very important. Most backend interviews at mid-senior level are 50% technical, 50% behavioral. Interviewers want to know how you handle incidents, collaborate across teams, and make architectural decisions.' },
    ],
  },
  {
    slug: 'qa-engineer',
    title: 'QA Engineer',
    aliases: ['qa engineer', 'test engineer', 'software tester'],
    headline: 'Ace Your QA Engineer Interview',
    description: 'Practice QA interview questions — test strategies, automation, bug reporting, and behavioral scenarios. Get AI feedback to sharpen your answers before interview day.',
    questions: [
      'How do you decide what to automate versus test manually?',
      'Walk me through your approach to writing a test plan for a new feature.',
      'Tell me about a critical bug you found that others missed.',
      'How would you test a login form? List all test cases you can think of.',
      'Describe a time you had to push back on a release because the quality wasn\'t good enough.',
    ],
    competencies: ['Test Strategy', 'Automation (Selenium/Playwright)', 'Bug Analysis', 'Communication', 'Risk Assessment'],
    faq: [
      { q: 'What topics do QA engineer interviews cover?', a: 'QA interviews cover test design (equivalence partitioning, boundary testing), automation tools (Selenium, Playwright, Cypress), CI/CD integration, bug reporting, and behavioral questions about past quality work.' },
      { q: 'Do I need coding skills for QA interviews?', a: 'For automation QA roles, yes — expect Python or JavaScript scripting questions. For manual QA, the focus is test strategy and communication. AI coaching covers both tracks.' },
    ],
  },
  {
    slug: 'software-engineer',
    title: 'Software Engineer',
    aliases: ['software engineer', 'swe', 'software developer', 'developer'],
    headline: 'Land Your Software Engineer Role',
    description: 'Practice software engineering behavioral and system design interviews with AI. Get BARS-scored feedback on communication, problem-solving, and technical leadership — the areas that determine your level.',
    questions: [
      'Tell me about the most complex system you\'ve built. What trade-offs did you make?',
      'Describe a time you disagreed with a technical decision. How did you handle it?',
      'Walk me through how you\'d design a URL shortening service at scale.',
      'Tell me about a time you had to deliver under a tight deadline. What did you cut?',
      'How do you approach code reviews? What do you look for and what do you flag?',
    ],
    competencies: ['System Design', 'Technical Communication', 'Problem-Solving', 'Collaboration', 'Code Quality'],
    faq: [
      { q: 'What do software engineer behavioral interviews focus on?', a: 'Behavioral interviews for SWEs assess how you handle ambiguity, collaborate across teams, deal with failure, and make technical decisions. Companies like Amazon, Google, and Meta use structured competency frameworks — AI coaching helps you practice all of them.' },
      { q: 'How is a senior SWE interview different from a junior one?', a: 'Senior roles add system design rounds (1-2 rounds) and expect answers that show ownership, cross-team impact, and architectural thinking. Junior interviews focus more on problem-solving and learning agility.' },
    ],
  },
  {
    slug: 'data-scientist',
    title: 'Data Scientist',
    aliases: ['data scientist', 'ml researcher', 'data science'],
    headline: 'Prepare for Data Scientist Interviews',
    description: 'Practice data science interviews — statistics, ML concepts, case studies, and behavioral questions. Get instant AI feedback and improve your answers before the real thing.',
    questions: [
      'Explain the bias-variance trade-off and how you handle it in practice.',
      'How would you build a model to predict customer churn? Walk me through your approach.',
      'Tell me about a model you shipped. How did you measure its impact in production?',
      'What is the difference between precision and recall? When do you optimize for each?',
      'How would you explain a machine learning model\'s decision to a non-technical stakeholder?',
    ],
    competencies: ['Statistics & Probability', 'Machine Learning', 'Data Analysis', 'Communication', 'Business Acumen'],
    faq: [
      { q: 'What do data scientist interviews test?', a: 'Data science interviews test statistics and probability, machine learning (algorithms, model evaluation, feature engineering), SQL, programming (Python), and case studies where you apply data to a business problem.' },
      { q: 'How important are communication skills for data science roles?', a: 'Critical — most data scientists say communicating insights to stakeholders is harder than the modeling itself. AI coaching specifically improves how you explain technical concepts clearly, which is often what separates candidates at final rounds.' },
    ],
  },
  {
    slug: 'devops-engineer',
    title: 'DevOps Engineer',
    aliases: ['devops engineer', 'devops', 'site reliability engineer', 'sre', 'platform engineer'],
    headline: 'Ace Your DevOps Engineer Interview',
    description: 'Practice DevOps interview questions — CI/CD, infrastructure, incident response, and behavioral scenarios. Get BARS-scored AI feedback to sharpen your answers.',
    questions: [
      'Describe your approach to designing a CI/CD pipeline from scratch.',
      'Tell me about a production incident you handled. What was your process?',
      'How do you decide between managed services and self-hosted infrastructure?',
      'Explain the concept of infrastructure as code. What tools have you used?',
      'How would you reduce deployment frequency from monthly to daily?',
    ],
    competencies: ['CI/CD Pipelines', 'Infrastructure as Code', 'Incident Response', 'Cloud Platforms', 'Communication'],
    faq: [
      { q: 'What do DevOps interviews test?', a: 'DevOps interviews cover CI/CD design, container orchestration (Kubernetes, Docker), infrastructure as code (Terraform, Ansible), monitoring and observability, and incident management. Behavioral questions focus on on-call experience and cross-team collaboration.' },
      { q: 'What\'s the difference between a DevOps and an SRE interview?', a: 'SRE interviews lean heavier on reliability principles (error budgets, SLOs, toil reduction) and may include more system design. Both share CI/CD, infrastructure, and behavioral components that AI coaching helps you prepare for.' },
    ],
  },
  {
    slug: 'business-analyst',
    title: 'Business Analyst',
    aliases: ['business analyst', 'ba', 'business systems analyst'],
    headline: 'Land Your Business Analyst Role',
    description: 'Practice business analyst interviews — requirements gathering, stakeholder management, process improvement, and case questions. Get structured AI feedback on your answers.',
    questions: [
      'Tell me about a time you had to gather requirements from stakeholders who disagreed.',
      'How do you prioritize competing business requirements from multiple teams?',
      'Walk me through a process you mapped and improved. What was the before and after?',
      'Describe a time you had to communicate bad news to a project sponsor.',
      'How would you scope an MVP for a new internal reporting tool?',
    ],
    competencies: ['Requirements Gathering', 'Stakeholder Management', 'Process Analysis', 'Communication', 'Problem-Solving'],
    faq: [
      { q: 'What do business analyst interviews focus on?', a: 'BA interviews test requirements elicitation, process modeling, stakeholder communication, data analysis, and project delivery. Behavioral questions (tell me about a time…) make up the majority — AI coaching helps you structure compelling STAR answers.' },
      { q: 'Do I need technical skills for a BA interview?', a: 'Depends on the company. Technical BAs need SQL, basic data tools, and familiarity with Agile/Scrum. Business-focused BAs need strong communication and process skills. AI coaching covers both the technical and behavioral components.' },
    ],
  },
  {
    slug: 'ux-designer',
    title: 'UX Designer',
    aliases: ['ux designer', 'ui ux designer', 'product designer', 'ui designer'],
    headline: 'Ace Your UX Designer Interview',
    description: 'Practice UX design interviews — portfolio walkthroughs, design challenges, user research, and behavioral questions. Get AI feedback to sharpen how you communicate your design process.',
    questions: [
      'Walk me through a project in your portfolio. What was your design process?',
      'Tell me about a time user research changed the direction of a design decision.',
      'How do you handle disagreements between your design vision and engineering constraints?',
      'Design an onboarding flow for a new fitness app. What questions would you ask first?',
      'Describe how you measure whether a design change was successful.',
    ],
    competencies: ['Design Thinking', 'User Research', 'Prototyping', 'Communication', 'Stakeholder Collaboration'],
    faq: [
      { q: 'What do UX designer interviews typically cover?', a: 'UX interviews include a portfolio review, a design challenge (sometimes take-home, sometimes live), behavioral questions about past projects, and process questions (how do you approach user research?). Communication and storytelling matter as much as design craft.' },
      { q: 'How do I prepare for the behavioral part of a UX interview?', a: 'Use the STAR method (Situation, Task, Action, Result) for past-project questions. Interviewers want to understand your decision-making, collaboration style, and how you handled constraints. AI coaching helps you practice until your stories are clear and concise.' },
    ],
  },
  {
    slug: 'machine-learning-engineer',
    title: 'Machine Learning Engineer',
    aliases: ['machine learning engineer', 'ml engineer', 'ai engineer', 'mle'],
    headline: 'Prepare for ML Engineer Interviews',
    description: 'Practice machine learning engineering interviews — model deployment, MLOps, system design, and behavioral questions. Get AI feedback to improve your answers before interview day.',
    questions: [
      'How have you moved an ML model from notebook to production? Walk me through the steps.',
      'Tell me about a time a model worked well offline but underperformed in production.',
      'How would you design an ML system for real-time fraud detection?',
      'What\'s your approach to monitoring model drift after deployment?',
      'Explain a machine learning architecture decision you made and the trade-offs involved.',
    ],
    competencies: ['MLOps & Deployment', 'Model Architecture', 'System Design', 'Python & Tooling', 'Communication'],
    faq: [
      { q: 'How is an ML engineer interview different from a data scientist interview?', a: 'ML engineers are evaluated more on engineering skills — software design, system architecture, deployment pipelines, and production reliability. Data scientists focus more on statistics and modeling. Both roles have behavioral rounds that AI coaching helps you prepare for.' },
      { q: 'What technical topics do ML engineer interviews cover?', a: 'Expect questions on model serving (latency, throughput), feature stores, training pipelines, monitoring (data drift, model degradation), and coding. Behavioral questions focus on cross-functional collaboration and production incident handling.' },
    ],
  },
];

const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');

export const getCareerBySlug = (slug) =>
  CAREER_PAGES.find((career) => career.slug === String(slug || '').toLowerCase()) || null;

export const getCareerByQuery = (query) => {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) return null;

  return (
    CAREER_PAGES.find((career) => {
      if (normalize(career.title) === normalizedQuery) return true;
      return career.aliases.some((alias) => normalize(alias) === normalizedQuery);
    }) || null
  );
};

export const getCareerPages = () => CAREER_PAGES.slice();
